import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import { Grid, FullScreenToggle, Btn, Toolbar, IEditDeleteButtonsRendererOnClickParams, IGridComponent, Action, Spacer, CellDoubleClickedEvent, ICellRendererParams, CellRendererSelectorFunc } from '@samc/centricity-core';
import { IPackageDocumentListResponse, IPackageDocumentSetting, IIsArchiveRestoreAgGridColumnRendererOnClickParams } from './Interfaces';
import { PackageDocumentsDataSource } from './DataSources';
import { AjaxHelper, IPaginatedList } from '@samc/common';
import OpenDocumentSideDrawer from './Components/OpenDocumentSideDrawer.vue';
import EditDocumentModalComponent from './Components/EditDocumentModal.vue';
import IsArchiveAgGridColumnRenderer from './Components/IsArchiveAgGridColumnRenderer.vue';
import { IDocumentType } from '../../Interfaces';

@Component({
  name: 'package-document',
  components: { FullScreenToggle, Btn, Grid, Toolbar, EditDocumentModalComponent, Action, Spacer }
})
export default class PackageDocumentComponent extends Vue {
  @Prop({ required: true }) public packageDocumentSetting!: IPackageDocumentSetting;
  @Prop({ required: true }) public verificationToken!: string;
  @Prop({ required: true }) public dataImportUrl!: string;
  @Prop({ default: 'Document' }) public exportFileName!: string;
  @Prop({ default: 0 }) public refreshDocumentGrid!: number;

  public loaded = false;

  public get packageType(): 'buy' | 'sell' { return this.packageDocumentSetting.type === 'BuyPackage' ? 'buy' : 'sell'; }
  public get queryParams(): any { return { packageType: this.packageType, packageId: this.packageDocumentSetting.packageId }; }
  public shouldDisable = false;
  public dataSource: PackageDocumentsDataSource| null = null;
  public documentTypes: IDocumentType[] = [];
  public documentGridKey: number = 0;

  public getDeleteRenderer: CellRendererSelectorFunc = (params) => {
    return {
      component: 'iconButtonRenderer',
      params: {
        onClick: this.onDeletePackageDocumentClick.bind(this),
        icon: params.data.isArchived ? 'icon-trash' : 'icon-archive'
      }
    }
  }

  public packageLoansGrid(): IGridComponent { return this.$refs.documentGrid as any; }

  public async mounted() {
    this.verificationToken ??= await this.$verificationToken();
    if (this.packageDocumentSetting.packageId !== null) {
      this.dataSource = new PackageDocumentsDataSource(this.packageDocumentSetting.packageId, this.verificationToken, this.packageType);
    }

    const helper = new AjaxHelper('/datasource/documenttypes', this.verificationToken, null, null);
    const result = await helper.getData<IPaginatedList<IDocumentType>>(``, { isActive: true });
    this.documentTypes = result.items;
    this.loaded = true;
  }

  public async AddDocument() {
    this.shouldDisable = true;
    const result = await this.$sideDrawer<boolean>({
      title: 'Upload New Document(s)',
      actionTitle: 'Apply',
      component: OpenDocumentSideDrawer,
      props: {
        packageDocumentSetting: this.packageDocumentSetting,
        verificationToken: this.verificationToken,
        documentTypes: this.documentTypes,
        packageType: this.packageType
      }
    });

    if (result !== undefined) {
      this.packageLoansGrid().refreshDatasource();
    }
    this.shouldDisable = false;
  }

  public async onEditPackageDocumentClick(e: IEditDeleteButtonsRendererOnClickParams<IPackageDocumentListResponse>) {
    let result = await this.$modal<boolean>({
      component: EditDocumentModalComponent,
      title: 'Edit Document',
      width: 450,
      props: {
        id: e.data.id,
        verificationToken: this.verificationToken,
        packageType: this.packageType,
        packageId: this.packageDocumentSetting.packageId
      }
    });
    if (result !== undefined) {
      this.packageLoansGrid().refreshDatasource();
    }
  }

  public hideEditPackageDocument(data: IPackageDocumentListResponse): boolean {
    return data.isArchived;
  }

  public hideIsArchivedButton(data: IPackageDocumentListResponse): boolean {
    return !data.isArchived;
  }

  public async onRestorePackageDocumentClick(e: IIsArchiveRestoreAgGridColumnRendererOnClickParams<IPackageDocumentListResponse>) {
    try {
      this.$loading('Undo document archive');
      const helper = new AjaxHelper(`/packagedocument/document/${e.data.id}`, this.verificationToken, null, null);
      let response = await helper.postData<boolean>('restore', { id: e.data.id, modifiedBy: $user?.name }, this.queryParams);
      if (response) {
        $alert.success(`Document restored.`, `Document with name ${e.data.documentName} restored.`);
        this.packageLoansGrid().refreshDatasource();
      }
    }
    catch (err: any) {
      this.$alert.error('An error occurred undo document archive.');
    }
    finally {
      this.$loading();
    }
  }

  public async onDeletePackageDocumentClick(e: IEditDeleteButtonsRendererOnClickParams<IPackageDocumentListResponse>) {
    const result = await this.$alert<boolean>({
      type: 'warning',
      title: `Are you sure you want to ${e.data.isArchived ? 'remove' : 'archive'} this document?`,
      button: { data: true, text: e.data.isArchived ? 'Remove document' : 'Archive document' },
      link: { data: false, text: 'Cancel' }
    });

    if (!result) return;

    this.$loading(e.data.isArchived ? 'Removing document' : 'Archiving document');
    try {
      const helper = new AjaxHelper('/packagedocument/document/', this.verificationToken, null, null);

      const response = await helper.delete<boolean>('', { ...this.queryParams, id: e.data.id });
      if (!response) {
        this.$alert.error(`An error occurred ${e.data.isArchived ? 'Removing' : 'archiving'} document.`);
        return;
      }
      this.packageLoansGrid().refreshDatasource();
      $alert.success(`Document ${e.data.isArchived ? 'Removed.' : 'Archived.'}`, `Document with name ${e.data.documentName} ${e.data.isArchived ? 'Removed.' : 'Archived.'}`);
    }
    catch (err: any) {
      this.$alert.error(`An error occurred ${e.data.isArchived ? 'Removing' : 'archiving'} document.`);
      this.$alert.error('');
    }
    finally {
      this.$loading();
    }
  }

  public async onRowDoubleClick(e: CellDoubleClickedEvent) {
    if (e !== undefined) {
      this.$emit('download-document-click', e);
    }
  }

  public onDownloadDocumentClick(e: IEditDeleteButtonsRendererOnClickParams<IPackageDocumentListResponse>) {
    if (e !== undefined) {
      this.$emit('download-document-click', e);
    }
  }

  @Watch('refreshDocumentGrid')
  public async refreshDocumentGridChange(newValue: number) {
    if (newValue > 0 && this.packageLoansGrid()) {
      await this.packageLoansGrid().refreshDatasource();
    }
  }
}
